// src/whatsappChat/api/templateHooks.js
// Templates screen. These endpoints return BotSense's own objects, without
// the { success, data } wrapper used elsewhere.
import { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { waChatClient } from './client';
import { variableCount } from './content';

const TEMPLATES_PAGE = 50;

const withNumber = (numberId) => (numberId ? { number_id: numberId } : {});

// 'all' or a comma list comes back as one merged page; the cursor works only for one number.
export const isMultiNumber = (numberId) => numberId === 'all' || String(numberId || '').includes(',');

/** GET /templates/numbers. Numbers for the filter: [{ number_id, phone, name, sender_id, label }]. */
export function useWaTemplateNumbers() {
  return useQuery({
    queryKey: ['wa-template-numbers'],
    queryFn: async () => {
      const res = await waChatClient.get('/templates/numbers');
      return res.data?.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useWaTemplates({ numberId = '', status = '', category = '' }) {
  const query = useInfiniteQuery({
    queryKey: ['wa-templates', { numberId, status, category }],
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const res = await waChatClient.get('/templates', {
        params: {
          ...withNumber(numberId),
          ...(status ? { status: status.toLowerCase() } : {}),
          ...(category ? { category } : {}),
          limit: TEMPLATES_PAGE,
          ...(pageParam ? { starting_after: pageParam } : {}),
        },
      });
      return res.data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.has_more || isMultiNumber(numberId)) return undefined;
      const rows = lastPage.data || [];
      return lastPage.next_cursor || rows[rows.length - 1]?.id || undefined;
    },
  });

  const templates = useMemo(
    () => (query.data?.pages || []).flatMap((page) => page?.data || []),
    [query.data]
  );

  const pages = query.data?.pages || [];
  // Several numbers: no next page to load, so tell the user the list was cut short.
  const truncated = isMultiNumber(numberId) && Boolean(pages[pages.length - 1]?.has_more);

  return { ...query, templates, truncated };
}

/** One template with its current status and rejected_reason. */
export function useWaTemplate(id, numberId) {
  return useQuery({
    queryKey: ['wa-template', id, numberId || ''],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await waChatClient.get(`/templates/${id}`, { params: withNumber(numberId) });
      return res.data;
    },
    retry: 0,
  });
}

/** GET /templates/:id/test. The inputs a test send needs: { template, number_id, can_send, reason, fields }. */
export function useWaTemplateTest(id, numberId) {
  return useQuery({
    queryKey: ['wa-template-test', id, numberId || ''],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await waChatClient.get(`/templates/${id}/test`, { params: withNumber(numberId) });
      return res.data?.data;
    },
    retry: 0,
  });
}

/** POST /templates/:id/test. Never retried: a retry after a timeout can deliver the message twice. */
export function useWaSendTemplateTest() {
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const res = await waChatClient.post(`/templates/${id}/test`, payload);
      return res.data;
    },
    retry: 0,
  });
}

/** Creates the template and submits it to Meta in one call; it comes back PENDING. */
export function useWaCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await waChatClient.post('/templates', payload);
      return res.data;
    },
    retry: 0,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wa-templates'] });
    },
  });
}

/** POST /templates/media. Returns the media_row_id for components.header_media_row_id. */
export async function uploadWaTemplateMedia(file, mediaType, numberId) {
  const form = new FormData();
  form.append('file', file);
  const res = await waChatClient.post('/templates/media', form, {
    params: { media_type: mediaType, ...withNumber(numberId) },
  });
  return res.data?.media_row_id;
}

// ---- Create-form rules (the backend checks the same things and answers 400) ----

export const TEMPLATE_LIMITS = { header: 60, footer: 60, body: 1024, buttonText: 25, copyCode: 20, buttons: 10 };
export const BUTTON_CAPS = { URL: 2, PHONE_NUMBER: 1, COPY_CODE: 1 };
// BotSense caps per media type, in bytes
export const TEMPLATE_MEDIA_CAPS = {
  image: 5 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  document: 25 * 1024 * 1024,
};

export function bodyVariableError(body) {
  const text = String(body || '').trim();
  const all = text.match(/\{\{[^}]*\}\}/g) || [];
  if (all.some((token) => !/^\{\{\s*\d+\s*\}\}$/.test(token))) {
    return 'Variables must be numbered: {{1}}, {{2}} …';
  }
  const max = variableCount(text);
  const used = new Set(all.map((token) => Number(token.replace(/\D/g, ''))));
  for (let n = 1; n <= max; n += 1) {
    if (!used.has(n)) return 'Variables must run {{1}}, {{2}} … with no gaps.';
  }
  if (used.has(0)) return 'Variables start at {{1}}.';
  if (/^\{\{/.test(text) || /\}\}$/.test(text)) {
    return 'The body cannot start or end with a variable.';
  }
  return null;
}
